import { IonInfiniteScroll } from '@ionic/react'
import { motion } from 'framer-motion'
import { useMemo } from 'react'

import { useGetUnitScheduledHistoryByScheduled } from '@apps-common/apis/portal/schedules'
import { InfiniteLoader } from '@apps-common/components/common/InfiniteLoader'
import { PullRefresh } from '@apps-common/components/common/PullRefresh'
import { motionFadeIn } from '@apps-common/config/motion-variants'
import { IUnitScheduledQueueBasicModel } from '@apps-common/types/portal/unit-scheduled'

import { useMyMonitoringGetUnitScheduledHistoryByScheduled } from '@apps-common/apis/my-monitoring/schedules'
import { getControlType } from '@apps-common/apps/portal/pages/units/pages/manage/components/controls/control-type'
import { useControlTheme } from '@apps-common/hooks/useControlTheme'
import { ENV_APP_PROJECT } from '@apps-common/lib/environment'
import { HistorySkeletonCard } from './HistorySkeletonCard'
import { HistoryDate } from './components/HistoryDate'
import { HistoryResultMessageDisplay } from './components/HistoryResultMessageDisplay'
import { HistoryState } from './components/HistoryState'
import { HistoryStatus } from './components/HistoryStatus'

const TAKE = 15
const project = ENV_APP_PROJECT
interface IProps {
  scheduledId: number
  unitId?: string
}

export const ScheduleHistoryView: React.FC<IProps> = ({ scheduledId, unitId }) => {
  const { resolve } = useControlTheme()

  const useScheduleHistoryHook = useMemo(() => {
    if (project === 'my-monitoring') {
      return useMyMonitoringGetUnitScheduledHistoryByScheduled
    }

    return useGetUnitScheduledHistoryByScheduled
  }, [])

  const {
    isLoading: isLoadingById,
    isFetching: isFetchingById,
    isRefetching: isRefetchingById,
    isError: isErrorById,
    data: dataById,
    refetch: refetchById,
    fetchNextPage: fetchNextPageById,
    hasNextPage: hasNextPageById
  } = useScheduleHistoryHook({ scheduledId, take: TAKE, enabled: unitId === undefined })

  const data = useMemo(
    () =>
      dataById?.pages.reduce(
        (acc: IUnitScheduledQueueBasicModel[], curr: IUnitScheduledQueueBasicModel[]) => [...acc, ...(curr || [])],
        []
      ),
    [unitId, dataById]
  )

  const loading = isLoadingById || isErrorById

  return (
    <>
      <PullRefresh loading={isRefetchingById} callback={refetchById} />
      <div className='flex flex-col gap-[10px]'>
        {(loading || !data) && Array.from(Array(TAKE).keys()).map((i) => <HistorySkeletonCard key={i.toString()} />)}
        {!loading &&
          data &&
          data.map((d, i) => {
            const controlType = getControlType(d.controlTypeId, d.requiresControlState)
            const colors = resolve(controlType, d.desiredState.toLowerCase())

            return (
              <motion.div
                key={`schedule-history-card-${d.id}-${i}`}
                {...motionFadeIn(0.3)}
                className='relative overflow-hidden w-full bg-surface text-text border border-border rounded-[24px] pl-[15px] py-[10px] flex items-end justify-between'
              >
                <div className='flex flex-col gap-[10px] items-start justify-between'>
                  <HistoryDate date={d.dateAdded} />
                  <HistoryStatus result={d.result} msg={d.msg} />
                </div>
                <HistoryState colors={colors} state={d.desiredState} />
              </motion.div>
            )
          })}
        <HistoryResultMessageDisplay
          hasNoResults={!loading && !isFetchingById && !hasNextPageById && data && data.length === 0}
          isEndOfResults={!loading && !isFetchingById && !hasNextPageById && data && data.length > 0}
        />
      </div>
      <IonInfiniteScroll
        onIonInfinite={async (ev) => {
          if (!hasNextPageById) {
            ev.target.complete()

            return
          }

          await fetchNextPageById()

          ev.target.complete()
        }}
      >
        <InfiniteLoader key='trigger-history-infinite-loader' loading={!loading && isFetchingById} />
      </IonInfiniteScroll>
    </>
  )
}
